import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Ban, Copy, Flag, Loader2, SlidersHorizontal } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useI18n } from '@/lib/i18n'
import { useTRPC, type BannedUser, type ModerationPolicy, type ModerationUser, type ReportGroup } from '@/lib/trpc'

const REPORT_REASONS = [
  'illegal_content',
  'hate_or_harassment',
  'sexual_content',
  'threats_or_self_harm',
  'scam_spam_or_links',
  'something_else',
] as const

const BLOCKED_CATEGORIES = [
  'sexual/minors',
  'sexual',
  'hate/threatening',
  'hate',
  'harassment/threatening',
  'illicit/violent',
  'self-harm/instructions',
  'self-harm/intent',
] as const

const DEFAULT_ALERT_COUNT = 20
const DEFAULT_ALERT_WINDOW_MINUTES = 15
const REASON_MAX_LENGTH = 2000

type ReportReason = (typeof REPORT_REASONS)[number]
type BlockedCategory = (typeof BLOCKED_CATEGORIES)[number]
type Decision = 'dismiss' | 'withhold' | 'withhold_and_ban' | 'escalate'
type Message = { type: 'success' | 'error', text: string } | null

const DECISION_KEYS: Record<Decision, string> = {
  dismiss: 'dismiss',
  withhold: 'withhold',
  withhold_and_ban: 'withholdAndBan',
  escalate: 'escalate',
}

const STATUS_KEYS: Record<ReportGroup['status'], string> = {
  open: 'open',
  dismissed: 'statusDismissed',
  actioned: 'statusActioned',
  escalated: 'statusEscalated',
}

const BANNABLE_SHAPES = new Set(['human', 'external'])

const SELECT_CLASS = 'h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground'

function moderationKey(key: string): string {
  return `admin.moderation.${key}`
}

function fill(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    template,
  )
}

function useDateFormat() {
  const { language } = useI18n()
  return (value: Date | string) => new Date(value).toLocaleString(language === 'zh' ? 'zh-CN' : 'en-US')
}

function availableDecisions(group: ReportGroup): Decision[] {
  const holdable = !group.legalHold
  if (group.status === 'open') {
    return group.targetKind === 'game'
      ? ['dismiss', 'escalate']
      : ['dismiss', 'withhold', 'withhold_and_ban', 'escalate']
  }
  if (group.status === 'escalated') {
    const decisions: Decision[] = group.targetKind === 'game'
      ? ['dismiss']
      : ['dismiss', 'withhold', 'withhold_and_ban']
    return holdable ? [...decisions, 'escalate'] : decisions
  }
  return holdable ? ['escalate'] : []
}

function Spinner({ className = 'size-4' }: { className?: string }) {
  const { t } = useI18n()
  return (
    <span role="status" className="inline-flex self-center">
      <Loader2 aria-hidden="true" className={`${className} animate-spin`} />
      <span className="sr-only">{t('admin.loading')}</span>
    </span>
  )
}

function MessageLine({ message }: { message: Message }) {
  if (!message)
    return null
  return (
    <p
      role={message.type === 'error' ? 'alert' : 'status'}
      className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}
    >
      {message.text}
    </p>
  )
}

export function ReportsSection() {
  const { t } = useI18n()
  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <h2 className="text-center text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
          {t(moderationKey('nav'))}
        </h2>
        <ReportQueue />
        <BannedAccountsCard />
        <ThresholdsCard />
      </div>
    </div>
  )
}

function ReportQueue() {
  const trpc = useTRPC()
  const { t } = useI18n()
  const [status, setStatus] = useState<'open' | 'all'>('open')
  const [reason, setReason] = useState<ReportReason | ''>('')
  const reports = useInfiniteQuery(trpc.moderationAdminRouter.listReports.infiniteQueryOptions(
    { status, limit: 10, reason: reason || undefined },
    { getNextPageParam: page => page.nextCursor ?? undefined },
  ))
  const groups = reports.data?.pages.flatMap(page => page.groups) ?? []

  return (
    <Card data-testid="report-queue">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2">
          <Flag className="size-5" />
          {t(moderationKey('nav'))}
        </CardTitle>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="report-status-filter"
            aria-label={t(moderationKey('statusFilter'))}
            className={SELECT_CLASS}
            value={status}
            onChange={event => setStatus(event.target.value === 'all' ? 'all' : 'open')}
          >
            <option value="open">{t(moderationKey('open'))}</option>
            <option value="all">{t(moderationKey('all'))}</option>
          </select>
          <select
            data-testid="report-reason-filter"
            aria-label={t(moderationKey('reasonFilter'))}
            className={SELECT_CLASS}
            value={reason}
            onChange={event => setReason(REPORT_REASONS.find(item => item === event.target.value) ?? '')}
          >
            <option value="">{t(moderationKey('allReasons'))}</option>
            {REPORT_REASONS.map(item => (
              <option key={item} value={item}>{t(`admin.moderation.reasons.${item}`)}</option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {reports.isPending && (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            {t('admin.loading')}
          </p>
        )}
        {reports.isError && (
          <div className="space-y-2">
            <p role="alert" className="text-sm text-destructive">{t(moderationKey('loadError'))}</p>
            <Button type="button" variant="outline" size="sm" onClick={() => reports.refetch()}>
              {t(moderationKey('retry'))}
            </Button>
          </div>
        )}
        {reports.isSuccess && groups.length === 0 && (
          <p className="text-sm text-muted-foreground">{t(moderationKey('noReports'))}</p>
        )}
        {groups.map(group => <ReportCard key={group.key} group={group} />)}
        {reports.hasNextPage && (
          <Button
            type="button"
            variant="outline"
            disabled={reports.isFetchingNextPage}
            onClick={() => reports.fetchNextPage()}
          >
            {t(moderationKey('loadMore'))}
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

function authorLabel(author: ReportGroup['author']): string {
  if (!author)
    return '—'
  const parts: string[] = [author.shape]
  if (author.modelName && author.shape !== 'human')
    parts.push(author.modelName)
  if (author.userId)
    parts.push(author.steam ? 'steam' : 'game_website')
  if (author.username)
    parts.push(`@${author.username}`)
  else if (author.email)
    parts.push(author.email)
  if (author.status === 'banned')
    parts.push('banned')
  return parts.join(' · ')
}

function reporterLabel(reporter: ReportGroup['notes'][number]['reporter']): string {
  if (!reporter)
    return '—'
  return reporter.username ? `@${reporter.username}` : reporter.email ?? reporter.id
}

function checkLabel(check: NonNullable<ReportGroup['checkAtCommit']>): string {
  const summary = [check.outcome, check.source, check.classifierModel].filter(Boolean).join(' · ')
  const scores = check.topScores.map(score => `${score.category} ${score.score.toFixed(2)}`).join(', ')
  return scores ? `${summary} (${scores})` : summary
}

function ReportContext({ context }: { context: ReportGroup['context'] }) {
  const { t } = useI18n()
  const sides = (['before', 'after'] as const).flatMap(side => (context[side] ? [{ side, speech: context[side] }] : []))
  if (sides.length === 0)
    return null
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium">{t(moderationKey('context'))}</p>
      <ul className="space-y-1 text-muted-foreground">
        {sides.map(({ side, speech }) => (
          <li key={side} className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
            {`${[
              t(moderationKey(side)),
              speech.seatPosition === null ? null : fill(t(moderationKey('seat')), { seat: speech.seatPosition }),
            ].filter(Boolean).join(' · ')}: ${speech.text}`}
          </li>
        ))}
      </ul>
    </div>
  )
}

function ReportCard({ group }: { group: ReportGroup }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const formatDate = useDateFormat()
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const [keyword, setKeyword] = useState<string | null>(null)
  const [message, setMessage] = useState<Message>(null)
  const [copied, setCopied] = useState(false)

  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: trpc.moderationAdminRouter.listReports.pathKey() }),
    queryClient.invalidateQueries({ queryKey: trpc.moderationAdminRouter.listBannedUsers.pathKey() }),
  ])
  const decide = useMutation({
    ...trpc.moderationAdminRouter.decideReport.mutationOptions(),
    onSuccess: async (result) => {
      setReason('')
      setTouched(false)
      setMessage(result.banned === false ? { type: 'error', text: t(moderationKey('banFailed')) } : null)
      await refresh()
    },
    onError: () => setMessage({ type: 'error', text: t(moderationKey('actionError')) }),
  })
  const release = useMutation({
    ...trpc.moderationAdminRouter.releaseLegalHold.mutationOptions(),
    onSuccess: async () => {
      setReason('')
      setTouched(false)
      setMessage(null)
      await refresh()
    },
    onError: () => setMessage({ type: 'error', text: t(moderationKey('actionError')) }),
  })
  const addKeyword = useMutation({
    ...trpc.queryRouter.addBlockedKeyword.mutationOptions(),
    onSuccess: () => {
      setKeyword(null)
      setMessage({ type: 'success', text: t(moderationKey('keywordAdded')) })
    },
    onError: () => setMessage({ type: 'error', text: t(moderationKey('actionError')) }),
  })

  const pending = decide.isPending || release.isPending
  const missingReason = touched && !reason.trim()
  const reasonId = `report-reason-${group.key}`
  const reasonErrorId = `${reasonId}-error`
  const banHintId = `${reasonId}-ban-hint`
  const keywordFormId = `${reasonId}-keyword`
  const decisions = availableDecisions(group)
  const bannable = !!group.author?.userId && BANNABLE_SHAPES.has(group.author.shape)
  const showReason = decisions.length > 0 || group.legalHold
  const activeRedaction = group.redaction && !group.redaction.liftedAt ? group.redaction : null

  function withReason(run: (value: string) => void) {
    const value = reason.trim()
    if (!value) {
      setTouched(true)
      return
    }
    setMessage(null)
    run(value)
  }

  async function copyGameId() {
    try {
      await navigator.clipboard.writeText(group.gameId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    }
    catch {
      setCopied(false)
    }
  }

  function submitKeyword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const value = keyword?.trim()
    if (value)
      addKeyword.mutate({ keyword: value, field: 'speech' })
  }

  return (
    <article
      data-testid="report-card"
      data-target-key={group.targetKey}
      className="space-y-3 rounded-lg border border-border bg-background p-4"
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <strong>{fill(t(moderationKey(group.count === 1 ? 'reportCountOne' : 'reportCount')), { count: group.count })}</strong>
        <span className="text-muted-foreground">·</span>
        <span>
          {group.reasons
            .map(item => `${t(`admin.moderation.reasons.${item.reason}`)}${item.count > 1 ? ` ×${item.count}` : ''}`)
            .join(', ')}
        </span>
        <span className="text-muted-foreground">·</span>
        <time dateTime={new Date(group.newestAt).toISOString()}>{formatDate(group.newestAt)}</time>
        <Badge variant="outline">{t(moderationKey(STATUS_KEYS[group.status]))}</Badge>
        {group.autoWithheld && <Badge variant="secondary">{t(moderationKey('autoWithheld'))}</Badge>}
        {group.legalHold && <Badge variant="destructive">{t(moderationKey('legalHold'))}</Badge>}
        {activeRedaction && (
          <Badge variant="outline">{fill(t(moderationKey('withheldNow')), { reason: activeRedaction.reason })}</Badge>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        <code className="break-all text-foreground" dir="ltr">{group.gameId}</code>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={t(moderationKey('copyGameId'))}
          title={copied ? t(moderationKey('copied')) : t(moderationKey('copyGameId'))}
          onClick={copyGameId}
        >
          <Copy aria-hidden="true" className="size-4" />
        </Button>
        <span aria-live="polite" className="sr-only">{copied ? t(moderationKey('copied')) : ''}</span>
        <span>·</span>
        {group.targetKind === 'game'
          ? <span>{t(moderationKey('wholeGame'))}</span>
          : (
              <>
                {group.roundNumber !== null && <span>{fill(t(moderationKey('round')), { round: group.roundNumber })}</span>}
                {group.seatPosition !== null && <span>{fill(t(moderationKey('seat')), { seat: group.seatPosition })}</span>}
                <span>·</span>
                <span data-testid="report-author">{authorLabel(group.author)}</span>
              </>
            )}
      </div>

      {group.checkAtCommit && (
        <p className="text-sm">
          <span className="font-medium">{`${t(moderationKey('commitCheck'))}: `}</span>
          <span>{checkLabel(group.checkAtCommit)}</span>
        </p>
      )}

      {group.text && (
        <blockquote
          data-testid="report-text"
          className="whitespace-pre-wrap break-words border-l-2 border-border pl-3 text-sm [overflow-wrap:anywhere]"
        >
          {group.text}
        </blockquote>
      )}

      <ReportContext context={group.context} />

      {group.notes.length > 0 && (
        <div className="space-y-1 text-sm">
          <p className="font-medium">{t(moderationKey('notes'))}</p>
          <ul className="space-y-1">
            {group.notes.map(note => (
              <li key={note.reportId} className="break-words [overflow-wrap:anywhere]">
                <span>{`“${note.note}”`}</span>
                <span className="text-muted-foreground">
                  {` — ${reporterLabel(note.reporter)} · ${t(`admin.moderation.reasons.${note.reason}`)} · ${note.offering}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {group.decision && (
        <div className="space-y-1 rounded-md bg-muted/40 p-3 text-sm">
          <p>
            <span className="font-medium">{t(moderationKey(DECISION_KEYS[group.decision.decision]))}</span>
            {group.decision.decidedAt && (
              <span className="text-muted-foreground">
                {' · '}
                {fill(t(moderationKey('decidedBy')), {
                  admin: group.decision.decidedBy?.email ?? group.decision.decidedBy?.id ?? '—',
                  time: formatDate(group.decision.decidedAt),
                })}
              </span>
            )}
          </p>
          {group.decision.reason && (
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{group.decision.reason}</p>
          )}
        </div>
      )}

      {showReason && (
        <div className="space-y-2">
          <Label htmlFor={reasonId}>{t(moderationKey('decisionReason'))}</Label>
          <Textarea
            id={reasonId}
            data-testid="report-decision-reason"
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => {
              setReason(event.target.value)
              setMessage(null)
            }}
            aria-invalid={missingReason}
            aria-describedby={missingReason ? reasonErrorId : undefined}
            disabled={pending}
            rows={2}
          />
          {missingReason && <p id={reasonErrorId} role="alert" className="text-sm text-destructive">{t(moderationKey('reasonRequired'))}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {decisions.map(decision => (
          <Button
            key={decision}
            type="button"
            size="sm"
            data-testid={`report-decide-${decision}`}
            variant={decision === 'dismiss' ? 'outline' : decision === 'escalate' ? 'secondary' : 'destructive'}
            disabled={pending || (decision === 'withhold_and_ban' && !bannable)}
            aria-describedby={decision === 'withhold_and_ban' && !bannable ? banHintId : undefined}
            onClick={() => withReason(value => decide.mutate({
              gameId: group.gameId,
              targetKey: group.targetKey,
              decision,
              reason: value,
            }))}
          >
            {t(moderationKey(DECISION_KEYS[decision]))}
          </Button>
        ))}
        {group.targetKind === 'speech' && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="report-add-keyword"
            aria-expanded={keyword !== null}
            aria-controls={keyword !== null ? keywordFormId : undefined}
            onClick={() => setKeyword(keyword === null ? '' : null)}
          >
            {t(moderationKey('addKeyword'))}
          </Button>
        )}
        {group.legalHold && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            data-testid="report-release-hold"
            disabled={pending}
            onClick={() => withReason(value => release.mutate({ reportId: group.reportIds[0]!, reason: value }))}
          >
            {t(moderationKey('releaseHold'))}
          </Button>
        )}
        {pending && <Spinner />}
      </div>
      {decisions.includes('withhold_and_ban') && !bannable && (
        <p id={banHintId} className="text-xs text-muted-foreground">{t(moderationKey('banNotAllowed'))}</p>
      )}

      {keyword !== null && (
        <form id={keywordFormId} onSubmit={submitKeyword} className="flex flex-wrap gap-2">
          <Input
            data-testid="report-keyword-input"
            aria-label={t(moderationKey('keywordPlaceholder'))}
            placeholder={t(moderationKey('keywordPlaceholder'))}
            value={keyword}
            onChange={event => setKeyword(event.target.value)}
            className="max-w-xs"
          />
          <Button type="submit" size="sm" disabled={!keyword.trim() || addKeyword.isPending}>
            {t(moderationKey('addKeyword'))}
          </Button>
        </form>
      )}

      <MessageLine message={message} />
    </article>
  )
}

function AccountRow({
  user,
  action,
  disabled,
  onAction,
}: {
  user: Pick<ModerationUser, 'id' | 'email' | 'username' | 'status'> | BannedUser
  action: 'ban' | 'unban'
  disabled: boolean
  onAction: () => void
}) {
  const { t } = useI18n()
  const formatDate = useDateFormat()
  const banned = 'bannedAt' in user ? user : null
  return (
    <li data-testid={`account-row-${user.id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 last:border-0">
      <div className="min-w-0 text-sm">
        <p className="break-all" dir="ltr">
          {[
            user.email,
            user.username ? `@${user.username}` : null,
            'status' in user && user.status === 'banned' ? 'banned' : null,
          ].filter(Boolean).join(' · ')}
        </p>
        {banned?.reason && (
          <p className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {[
              banned.bannedAt ? formatDate(banned.bannedAt) : null,
              banned.bannedBy?.email ?? null,
              banned.reason,
            ].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>
      <Button
        type="button"
        size="sm"
        variant={action === 'ban' ? 'destructive' : 'outline'}
        data-testid={`account-${action}-${user.id}`}
        disabled={disabled}
        onClick={onAction}
      >
        {t(moderationKey(action))}
      </Button>
    </li>
  )
}

function BannedAccountsCard() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const [query, setQuery] = useState('')
  const [submitted, setSubmitted] = useState('')
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)
  const [message, setMessage] = useState<Message>(null)

  const search = useQuery({
    ...trpc.moderationAdminRouter.findUser.queryOptions({ query: submitted }),
    enabled: submitted.length > 0,
  })
  const banned = useQuery(trpc.moderationAdminRouter.listBannedUsers.queryOptions())
  const refresh = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: trpc.moderationAdminRouter.listBannedUsers.pathKey() }),
    queryClient.invalidateQueries({ queryKey: trpc.moderationAdminRouter.findUser.pathKey() }),
    queryClient.invalidateQueries({ queryKey: trpc.moderationAdminRouter.listReports.pathKey() }),
  ])
  const onSuccess = async () => {
    setReason('')
    setTouched(false)
    setMessage(null)
    await refresh()
  }
  const onError = () => setMessage({ type: 'error', text: t(moderationKey('actionError')) })
  const ban = useMutation({ ...trpc.moderationAdminRouter.banUser.mutationOptions(), onSuccess, onError })
  const unban = useMutation({ ...trpc.moderationAdminRouter.unbanUser.mutationOptions(), onSuccess, onError })
  const pending = ban.isPending || unban.isPending
  const missingReason = touched && !reason.trim()

  function act(kind: 'ban' | 'unban', userId: string) {
    const value = reason.trim()
    if (!value) {
      setTouched(true)
      return
    }
    setMessage(null)
    if (kind === 'ban')
      ban.mutate({ userId, reason: value })
    else
      unban.mutate({ userId, reason: value })
  }

  function submitSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitted(query.trim())
  }

  return (
    <Card data-testid="banned-accounts-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ban className="size-5" />
          {t(moderationKey('bannedAccounts'))}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={submitSearch} className="flex flex-wrap gap-2">
          <Input
            data-testid="account-search-input"
            aria-label={t(moderationKey('searchPlaceholder'))}
            placeholder={t(moderationKey('searchPlaceholder'))}
            value={query}
            onChange={event => setQuery(event.target.value)}
            className="min-w-0 flex-1"
            autoComplete="off"
            spellCheck={false}
          />
          <Button type="submit" variant="outline" data-testid="account-search-submit" disabled={!query.trim()}>
            {t(moderationKey('search'))}
          </Button>
        </form>

        <div className="space-y-2">
          <Label htmlFor="account-ban-reason">{t(moderationKey('reason'))}</Label>
          <Input
            id="account-ban-reason"
            data-testid="account-ban-reason"
            value={reason}
            maxLength={REASON_MAX_LENGTH}
            onChange={(event) => {
              setReason(event.target.value)
              setMessage(null)
            }}
            aria-invalid={missingReason}
            aria-describedby={missingReason ? 'account-ban-reason-error' : undefined}
            disabled={pending}
          />
          {missingReason && <p id="account-ban-reason-error" role="alert" className="text-sm text-destructive">{t(moderationKey('reasonRequired'))}</p>}
        </div>

        {submitted && (
          <div data-testid="account-search-results">
            {search.isFetching && <Spinner />}
            {search.isError && <p role="alert" className="text-sm text-destructive">{t(moderationKey('loadError'))}</p>}
            {search.isSuccess && search.data.length === 0 && (
              <p className="text-sm text-muted-foreground">{t(moderationKey('noUsers'))}</p>
            )}
            {search.isSuccess && search.data.length > 0 && (
              <ul>
                {search.data.map(user => (
                  <AccountRow
                    key={user.id}
                    user={user}
                    action={user.status === 'banned' ? 'unban' : 'ban'}
                    disabled={pending}
                    onAction={() => act(user.status === 'banned' ? 'unban' : 'ban', user.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        )}

        <div data-testid="banned-account-list" className="border-t border-border pt-3">
          {banned.isPending && <Spinner />}
          {banned.isError && <p role="alert" className="text-sm text-destructive">{t(moderationKey('loadError'))}</p>}
          {banned.isSuccess && banned.data.length === 0 && (
            <p className="text-sm text-muted-foreground">{t(moderationKey('noBannedUsers'))}</p>
          )}
          {banned.isSuccess && banned.data.length > 0 && (
            <ul>
              {banned.data.map(user => (
                <AccountRow
                  key={user.id}
                  user={user}
                  action="unban"
                  disabled={pending}
                  onAction={() => act('unban', user.id)}
                />
              ))}
            </ul>
          )}
        </div>

        <MessageLine message={message} />
      </CardContent>
    </Card>
  )
}

interface PolicyDraft {
  thresholds: Record<BlockedCategory, string>
  alertCount: string
  alertWindow: string
}

function toPolicyDraft(policy: ModerationPolicy): PolicyDraft {
  const thresholds = Object.fromEntries(BLOCKED_CATEGORIES.map((category) => {
    const value = policy.thresholds[category]
    return [category, value === undefined ? '' : String(value)]
  })) as Record<BlockedCategory, string>
  return {
    thresholds,
    alertCount: policy.uncheckedAlert ? String(policy.uncheckedAlert.count) : '',
    alertWindow: policy.uncheckedAlert ? String(policy.uncheckedAlert.windowMinutes) : '',
  }
}

function parseThreshold(value: string): number | null {
  if (!value.trim())
    return null
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : Number.NaN
}

function parseCount(value: string, fallback: number, max: number): number {
  if (!value.trim())
    return fallback
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= max ? parsed : Number.NaN
}

function ThresholdsCard() {
  const trpc = useTRPC()
  const { t } = useI18n()
  const policy = useQuery(trpc.queryRouter.getModerationPolicy.queryOptions())
  return (
    <Card data-testid="moderation-thresholds-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="size-5" />
          {t(moderationKey('thresholds'))}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {policy.isPending && <Spinner />}
        {policy.isError && <p role="alert" className="text-sm text-destructive">{t(moderationKey('loadError'))}</p>}
        {policy.data && <ThresholdsForm policy={policy.data.data} />}
      </CardContent>
    </Card>
  )
}

function ThresholdsForm({ policy }: { policy: ModerationPolicy }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { t } = useI18n()
  const [draft, setDraft] = useState<PolicyDraft>(() => toPolicyDraft(policy))
  const [message, setMessage] = useState<Message>(null)
  const save = useMutation({
    ...trpc.queryRouter.setModerationPolicy.mutationOptions(),
    onSuccess: (result) => {
      queryClient.setQueryData(trpc.queryRouter.getModerationPolicy.queryKey(), result)
      setDraft(toPolicyDraft(result.data))
      setMessage({ type: 'success', text: t(moderationKey('saved')) })
    },
    onError: () => setMessage({ type: 'error', text: t(moderationKey('actionError')) }),
  })

  const thresholds = BLOCKED_CATEGORIES.map(category => [category, parseThreshold(draft.thresholds[category])] as const)
  const alertSet = draft.alertCount.trim() !== '' || draft.alertWindow.trim() !== ''
  const alertCount = parseCount(draft.alertCount, DEFAULT_ALERT_COUNT, 10_000)
  const alertWindow = parseCount(draft.alertWindow, DEFAULT_ALERT_WINDOW_MINUTES, 1_440)
  const valid = thresholds.every(([, value]) => value === null || !Number.isNaN(value))
    && !Number.isNaN(alertCount)
    && !Number.isNaN(alertWindow)

  function update(next: Partial<PolicyDraft>) {
    setMessage(null)
    setDraft(previous => ({ ...previous, ...next }))
  }

  function setThreshold(category: BlockedCategory, value: string) {
    update({ thresholds: { ...draft.thresholds, [category]: value } })
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!valid)
      return
    save.mutate({
      thresholds: Object.fromEntries(thresholds.flatMap(([category, value]) => (value === null ? [] : [[category, value]]))),
      ...(alertSet ? { uncheckedAlert: { count: alertCount, windowMinutes: alertWindow } } : {}),
    })
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <ul className="space-y-2">
        {BLOCKED_CATEGORIES.map((category) => {
          const value = draft.thresholds[category]
          const usesThreshold = value !== ''
          const invalid = usesThreshold && Number.isNaN(parseThreshold(value))
          return (
            <li key={category} className="grid items-center gap-2 sm:grid-cols-[200px_180px_120px]">
              <code className="text-sm">{category}</code>
              <select
                aria-label={category}
                data-testid={`threshold-mode-${category}`}
                className={SELECT_CLASS}
                value={usesThreshold ? 'threshold' : 'flag'}
                onChange={event => setThreshold(category, event.target.value === 'threshold' ? '0.5' : '')}
              >
                <option value="flag">{t(moderationKey('classifierFlag'))}</option>
                <option value="threshold">{t(moderationKey('scoreThreshold'))}</option>
              </select>
              {usesThreshold && (
                <Input
                  aria-label={`${category} ${t(moderationKey('scoreThreshold'))}`}
                  data-testid={`threshold-value-${category}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  max={1}
                  step={0.01}
                  value={value}
                  aria-invalid={invalid}
                  onChange={event => setThreshold(category, event.target.value)}
                />
              )}
            </li>
          )
        })}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="unchecked-alert-count">{t(moderationKey('uncheckedAlertCount'))}</Label>
          <Input
            id="unchecked-alert-count"
            type="number"
            min={1}
            max={10_000}
            step={1}
            placeholder={String(DEFAULT_ALERT_COUNT)}
            value={draft.alertCount}
            aria-invalid={Number.isNaN(alertCount)}
            onChange={event => update({ alertCount: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="unchecked-alert-window">{t(moderationKey('uncheckedAlertWindow'))}</Label>
          <Input
            id="unchecked-alert-window"
            type="number"
            min={1}
            max={1_440}
            step={1}
            placeholder={String(DEFAULT_ALERT_WINDOW_MINUTES)}
            value={draft.alertWindow}
            aria-invalid={Number.isNaN(alertWindow)}
            onChange={event => update({ alertWindow: event.target.value })}
          />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" data-testid="thresholds-save" disabled={!valid || save.isPending}>
          {t(moderationKey('save'))}
        </Button>
        <MessageLine message={message} />
      </div>
    </form>
  )
}
